import {
    ApolloClient,
    ApolloQueryResult,
    DefaultContext,
    FetchResult,
    MutationOptions,
    OperationVariables,
    QueryOptions,
} from "@apollo/client/core";
import { graphQlManager } from "./server";

interface GraphQlClient {
    query<T = any, TVariables extends OperationVariables = OperationVariables>(
        options: QueryOptions<TVariables, T>,
    ): Promise<ApolloQueryResult<T>>;
    mutate<
        TData = any,
        TVariables extends OperationVariables = OperationVariables,
        TContext extends Record<string, any> = DefaultContext,
    >(
        options: MutationOptions<TData, TVariables, TContext>,
    ): Promise<FetchResult<TData>>;
}

export type GraphQlClientFunc = () => GraphQlClient;

/**
 * Provides an interface to customize GraphQL requests before they are processed.
 */
export class GraphQlClientWrapper implements GraphQlClient {
    constructor(
        private readonly options?: {
            onQuery?: (
                options: QueryOptions<OperationVariables, any>,
            ) => Partial<QueryOptions<OperationVariables, any>>;
            onMutate?: (
                options: MutationOptions<any, OperationVariables, DefaultContext>,
            ) => Partial<MutationOptions<any, OperationVariables, DefaultContext>>;
        },
        private readonly client: () => ApolloClient<any> = graphQlManager.getShadowGraphqlClient.bind(graphQlManager),
    ) {}

    query<T = any, TVariables extends OperationVariables = OperationVariables>(
        options: QueryOptions<TVariables, T>,
    ): Promise<ApolloQueryResult<T>> {
        const base = this.options?.onQuery ? this.options?.onQuery(options) : {};
        return this.client().query({
            ...base,
            ...options,
            context: {
                ...(base.context ?? {}),
                ...(options.context ?? {}),
            },
        });
    }

    mutate<
        TData = any,
        TVariables extends OperationVariables = OperationVariables,
        TContext extends Record<string, any> = DefaultContext,
    >(options: MutationOptions<TData, TVariables, TContext>): Promise<FetchResult<TData>> {
        const base = this.options?.onMutate ? this.options?.onMutate(options) : {};
        return this.client().mutate({
            ...base,
            ...options,
            context: {
                ...(base.context ?? {}),
                ...(options.context ?? {}),
            },
        });
    }
}
